/**
 * Manual Gantt edits sit on the capacity baseline.
 * `computeGanttActivitySchedules` always rebuilds generated terms.
 * `metadata.ganttPlanOverrides` replaces term, name, and order for touched stages
 * and keeps a stage on the chart when a later recalc drops it.
 */

import {
  GANTT_PLANNING_ACTIVITY_IDS,
  GANTT_PLANNING_ACTIVITY_LABELS,
  GANTT_PLAN_OVERRIDE_LABEL_MAX_LENGTH,
  GANTT_PLAN_OVERRIDE_SORT_MAX,
  GANTT_PLAN_OVERRIDE_SORT_MIN,
  type GanttActivityPlanOverride,
  type GanttPlanningActivityId,
} from '../constants/gantt-planning.constants';
import {
  formatGanttPlanningDate,
  parseGanttPlanningDate,
  type GanttActivitySchedule,
} from './gantt-planning.util';

export type GanttPlanOverrides = Partial<Record<GanttPlanningActivityId, GanttActivityPlanOverride>>;

export interface GanttPlanEditStamp {
  editedBy: string;
  editedAt: string;
}

export function isGanttPlanningActivityId(value: string): value is GanttPlanningActivityId {
  return (GANTT_PLANNING_ACTIVITY_IDS as readonly string[]).includes(value);
}

export function ganttPlanOverridesFromMetadata(metadata: unknown): GanttPlanOverrides {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {};
  return readGanttPlanOverrides((metadata as Record<string, unknown>)['ganttPlanOverrides']);
}

/** Drop broken rows. Used when reading stored metadata, not when validating a save. */
export function readGanttPlanOverrides(raw: unknown): GanttPlanOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: GanttPlanOverrides = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isGanttPlanningActivityId(key)) continue;
    const parsed = parseOverride(key, value, false);
    if (parsed) out[key] = parsed;
  }
  return out;
}

/**
 * Merge a partial patch onto stored overrides.
 * `null` removes one stage. A `null` patch clears every override.
 * Unchanged content keeps the previous author stamp.
 */
export function mergeGanttPlanOverrides(
  previous: unknown,
  patch: unknown,
  stamp: GanttPlanEditStamp,
): GanttPlanOverrides {
  if (patch === null) return {};
  if (patch === undefined) return readGanttPlanOverrides(previous);
  if (typeof patch !== 'object' || Array.isArray(patch)) {
    throw new Error('ganttPlanOverrides musí být objekt.');
  }
  const prev = readGanttPlanOverrides(previous);
  const next: GanttPlanOverrides = { ...prev };
  for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
    if (!isGanttPlanningActivityId(key)) {
      throw new Error(`ganttPlanOverrides.${key}: neznámá etapa.`);
    }
    if (value == null) {
      delete next[key];
      continue;
    }
    const normalized = parseOverride(key, value, true);
    if (!normalized) {
      throw new Error(`${GANTT_PLANNING_ACTIVITY_LABELS[key]}: neplatná úprava.`);
    }
    const prior = prev[key];
    if (prior && samePlanContent(prior, normalized)) {
      next[key] = prior;
    } else {
      const editedBy = stamp.editedBy.trim();
      next[key] = {
        ...normalized,
        ...(editedBy ? { editedBy } : {}),
        editedAt: stamp.editedAt,
      };
    }
  }
  return next;
}

/**
 * Overlay stored edits on a freshly computed baseline.
 * A stage with both manual dates stays even when this baseline no longer emits it.
 */
export function applyGanttPlanOverrides(
  schedules: readonly GanttActivitySchedule[],
  overrides: GanttPlanOverrides | null | undefined,
): GanttActivitySchedule[] {
  const byId = new Map(schedules.map((schedule) => [schedule.activityId, schedule]));
  const overrideMap = overrides ?? {};
  const out: GanttActivitySchedule[] = [];

  for (const activityId of GANTT_PLANNING_ACTIVITY_IDS) {
    const base = byId.get(activityId);
    const override = overrideMap[activityId];
    if (!override) {
      if (base) out.push({ ...base, manual: false });
      continue;
    }
    const plannedFrom = override.plannedFrom || base?.plannedFrom;
    const plannedTo = override.plannedTo || base?.plannedTo;
    if (!plannedFrom || !plannedTo) {
      if (base) out.push({ ...base, manual: false });
      continue;
    }
    out.push({
      activityId,
      label: override.label?.trim() || base?.label || GANTT_PLANNING_ACTIVITY_LABELS[activityId],
      plannedFrom,
      plannedTo,
      durationWorkingDays: base?.durationWorkingDays ?? 0,
      quantity: base?.quantity ?? 0,
      ...(typeof override.sortOrder === 'number' ? { sortOrder: override.sortOrder } : {}),
      manual: true,
    });
  }

  return out;
}

function samePlanContent(a: GanttActivityPlanOverride, b: GanttActivityPlanOverride): boolean {
  return a.plannedFrom === b.plannedFrom
    && a.plannedTo === b.plannedTo
    && (a.label ?? '') === (b.label ?? '')
    && (a.sortOrder ?? null) === (b.sortOrder ?? null);
}

function parseOverride(
  activityId: GanttPlanningActivityId,
  raw: unknown,
  strict: boolean,
): GanttActivityPlanOverride | null {
  const labelName = GANTT_PLANNING_ACTIVITY_LABELS[activityId];
  try {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      throw new Error(`${labelName}: úprava musí být objekt.`);
    }
    const source = raw as Record<string, unknown>;
    const plannedFrom = requireDate(source['plannedFrom'], `${labelName}: termín od`);
    const plannedTo = requireDate(source['plannedTo'], `${labelName}: termín do`);
    const from = parseGanttPlanningDate(plannedFrom);
    const to = parseGanttPlanningDate(plannedTo);
    if (!from || !to || from.getTime() > to.getTime()) {
      throw new Error(`${labelName}: termín od musí být před termínem do.`);
    }
    const parsed: GanttActivityPlanOverride = {
      plannedFrom: formatGanttPlanningDate(from),
      plannedTo: formatGanttPlanningDate(to),
    };
    if (source['label'] != null && String(source['label']).trim()) {
      const label = String(source['label']).trim();
      if (label.length > GANTT_PLAN_OVERRIDE_LABEL_MAX_LENGTH) {
        throw new Error(`${labelName}: název může mít nejvýše ${GANTT_PLAN_OVERRIDE_LABEL_MAX_LENGTH} znaků.`);
      }
      parsed.label = label;
    }
    if (source['sortOrder'] != null && source['sortOrder'] !== '') {
      const sortOrder = Number(source['sortOrder']);
      if (
        !Number.isInteger(sortOrder)
        || sortOrder < GANTT_PLAN_OVERRIDE_SORT_MIN
        || sortOrder > GANTT_PLAN_OVERRIDE_SORT_MAX
      ) {
        throw new Error(
          `${labelName}: pořadí musí být celé číslo ${GANTT_PLAN_OVERRIDE_SORT_MIN}–${GANTT_PLAN_OVERRIDE_SORT_MAX}.`,
        );
      }
      parsed.sortOrder = sortOrder;
    }
    if (!strict) {
      const editedBy = typeof source['editedBy'] === 'string' ? source['editedBy'].trim() : '';
      const editedAt = typeof source['editedAt'] === 'string' ? source['editedAt'].trim() : '';
      if (editedBy) parsed.editedBy = editedBy.slice(0, 200);
      if (editedAt) parsed.editedAt = editedAt.slice(0, 40);
    }
    return parsed;
  } catch (error) {
    if (strict) throw error;
    return null;
  }
}

function requireDate(value: unknown, fieldLabel: string): string {
  const text = typeof value === 'string' ? value.trim() : '';
  const parsed = parseGanttPlanningDate(text);
  if (!parsed) throw new Error(`${fieldLabel} zadejte jako dd.mm.yyyy.`);
  return formatGanttPlanningDate(parsed);
}
